import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

/**
 * SendAccountantMonthCard — owner picks a Year + Month from dropdowns and sends
 * that month's accountant report to the configured recipients. Defaults to the
 * last completed month. Backend: POST /coach/revenue/send-accountant-report.
 * Sachin 2026-09.
 */
export default function SendAccountantMonthCard() {
    const now = new Date();
    // Default to last completed month.
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [year, setYear] = useState(lastMonthDate.getFullYear());
    const [month, setMonth] = useState(lastMonthDate.getMonth() + 1); // 1-12
    const [sending, setSending] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [extra, setExtra] = useState(""); // extra emails typed for this send

    const years = useMemo(() => {
        const start = 2024;
        const end = now.getFullYear();
        const out = [];
        for (let y = end; y >= start; y--) out.push(y);
        return out;
    }, [now]);

    useEffect(() => {
        axios.get(`${API}/coach/revenue/accountant-recipients`, { withCredentials: true })
            .then((r) => setRecipients(r.data?.effective || r.data?.emails || []))
            .catch(() => {});
    }, []);

    const periodKey = `${year}-${String(month).padStart(2, "0")}`;
    const periodLabel = `${MONTHS[month - 1]} ${year}`;
    // Extra emails typed for this send (comma/space/newline separated).
    const extraEmails = extra.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const badEmails = extraEmails.filter((e) => !EMAIL_RE.test(e));
    // Everyone who will receive this send = saved recipients + valid typed ones.
    const allRecipients = Array.from(new Set([...recipients, ...extraEmails.filter((e) => EMAIL_RE.test(e))]));
    // Don't allow sending a month that hasn't finished yet.
    const isFuture = year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);
    const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;

    const send = async () => {
        if (isFuture) { toast.error("Pick a month that has already happened"); return; }
        if (badEmails.length) { toast.error("Check the email address", { description: badEmails.join(", ") }); return; }
        if (allRecipients.length === 0) { toast.error("Add at least one email to send to"); return; }
        const who = allRecipients.join(", ");
        if (!window.confirm(`Send the ${periodLabel} accountant report to ${who}?`)) return;
        setSending(true);
        try {
            const r = await axios.post(`${API}/coach/revenue/send-accountant-report`,
                { period_key: periodKey, recipients: allRecipients }, { withCredentials: true });
            const sent = r.data?.sent || r.data?.sent_to || [];
            const failed = (r.data?.failed || []).map((f) => (typeof f === "string" ? f : f.email)).filter(Boolean);
            if (sent.length) toast.success(`Sent ${periodLabel} report`, { description: sent.join(", ") });
            else if (failed.length) toast.error("Send failed", { description: failed.join(", ") });
            else toast.message(`Nothing to send for ${periodLabel}`);
        } catch (e) {
            toast.error("Couldn't send report", { description: e?.response?.data?.detail || "Try again." });
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="send-accountant-month-card" className="bg-zinc-900/60 border border-zinc-700 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-brand-red" />
                <h3 className="text-sm font-semibold tracking-wide">Send accountant report</h3>
            </div>
            <p className="text-xs text-zinc-400 mb-4">
                Pick any month and send its full revenue report. Add extra emails below to CC someone for this send.
            </p>
            <div className="flex flex-wrap items-end gap-3">
                <label className="block">
                    <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Month</span>
                    <select
                        data-testid="accountant-month-select"
                        value={month}
                        onChange={(e) => setMonth(Number(e.target.value))}
                        className="bg-black border border-zinc-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-brand-red [color-scheme:dark]"
                    >
                        {MONTHS.map((m, i) => (
                            <option key={m} value={i + 1}>{m}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Year</span>
                    <select
                        data-testid="accountant-year-select"
                        value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="bg-black border border-zinc-700 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-brand-red [color-scheme:dark]"
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    data-testid="accountant-send-month-btn"
                    onClick={send}
                    disabled={sending || isFuture || badEmails.length > 0 || allRecipients.length === 0}
                    className="flex items-center gap-2 bg-brand-red hover:bg-red-700 disabled:opacity-50 rounded px-4 py-2 text-sm font-semibold"
                >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send {periodLabel}
                </button>
            </div>
            <label className="block mt-4">
                <span className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">Also send to (optional)</span>
                <input
                    type="text"
                    data-testid="accountant-extra-emails"
                    value={extra}
                    onChange={(e) => setExtra(e.target.value)}
                    placeholder="accountant@example.com, another@example.com"
                    className="w-full max-w-md bg-black border border-zinc-700 text-white text-sm rounded px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-brand-red"
                />
                <span className="block text-[11px] text-zinc-500 mt-1">
                    {badEmails.length > 0
                        ? <span className="text-amber-400">Not a valid email: {badEmails.join(", ")}</span>
                        : <>Goes to: <span className="text-zinc-300">{allRecipients.join(", ") || "—"}</span></>}
                </span>
            </label>
            {isFuture && (
                <p className="text-[11px] text-amber-400 mt-2">That month hasn't happened yet — pick an earlier month.</p>
            )}
            {isCurrent && (
                <p className="text-[11px] text-zinc-500 mt-2">This is the current month (still in progress) — figures will be partial.</p>
            )}
        </div>
    );
}
